import { useEffect, useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { message } from "antd";
import type {
    CopyableOption,
    DetailJsonBlock,
    DetailJsonColumn,
    DetailJsonEventContext,
    DetailJsonProps,
    LayoutItem,
    LinkOption
} from "./type";
import { resolveBoolean, useBlockLayout } from "./useBlockLayout";
import TitleBlock from "./titleBlock";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface DetailJsonSlotContext {
    block?: DetailJsonBlock;
    item?: DetailJsonColumn;
    value?: unknown;
    /** 当前字段在 block.list 中的索引；section 插槽固定为 -1。 */
    colIndex: number;
    columnCount: number;
    columnLabelWidthMap: number[];
    blockLayoutMaps: LayoutItem[][];
}

export interface DetailJsonReactProps extends DetailJsonProps {
    slots?: Record<string, (context: DetailJsonSlotContext) => ReactNode>;
}

/** 详情 JSON 展示器，保留 Vue 版分区、跨列、复制、链接和动态显隐能力。 */
export default function DetailJson({ columns = [], data, config, slots = {} }: DetailJsonReactProps) {
    const navigate = useNavigate();
    const [columnCount, setColumnCount] = useState(() => getColumnCount());

    useEffect(() => {
        const onResize = () => setColumnCount(getColumnCount());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const visibleBlocks = useMemo<DetailJsonBlock[]>(
        () =>
            columns
                .filter(block => resolveBoolean(block.show))
                .map(block => ({ ...block, list: (block.list ?? []).filter(item => resolveBoolean(item.show)) })),
        [columns]
    );
    const { blockLayoutMaps, columnLabelWidthMap, getLabelStyle } = useBlockLayout({
        columnCount,
        blocks: visibleBlocks,
        config
    });
    const defaultValue = config?.defaultValue ?? "--";

    const resolveValue = (item: DetailJsonColumn) => {
        if (item.value !== undefined) {
            try {
                return typeof item.value === "function" ? item.value() : item.value;
            } catch {
                // 与旧 Vue 一致：单字段 getter 异常只回退占位符，不阻断整个详情页。
                return defaultValue;
            }
        }
        if (!item.key) return defaultValue;
        let current: any = data;
        for (const key of item.key.split(".")) current = current?.[key];
        return current === undefined || current === null ? defaultValue : current;
    };

    const eventContext = (
        item: DetailJsonColumn,
        value: unknown,
        event: MouseEvent<HTMLDivElement>
    ): DetailJsonEventContext => ({
        column: item,
        value,
        data,
        event: event.nativeEvent
    });

    const handleCopy = async (item: DetailJsonColumn, value: unknown, event: MouseEvent<HTMLDivElement>) => {
        const option = normalizeCopyable(item.copyable);
        if (!option?.enabled) return false;
        const context = eventContext(item, value, event);
        if (option.beforeCopy?.(context) === false) return true;
        const text = typeof option.text === "function" ? option.text(context) : (option.text ?? String(value ?? ""));
        if (text) {
            try {
                await navigator.clipboard.writeText(text);
                notifyOperationSuccess("复制成功");
                option.afterCopy?.(context);
            } catch {
                message.error("复制失败");
            }
        }
        return true;
    };

    const handleLink = (item: DetailJsonColumn, value: unknown, event: MouseEvent<HTMLDivElement>) => {
        const option = normalizeLink(item.link);
        if (!option || option.enabled === false) return false;
        const context = eventContext(item, value, event);
        if (option.beforeLink?.(context) === false) return true;
        const target = typeof option.href === "function" ? option.href(context) : option.href;
        if (target) {
            if (typeof target === "string" && /^(https?:|mailto:|tel:)/.test(target))
                window.open(target, option.target ?? "_self");
            else if (option.target === "_blank") window.open(String(target), "_blank");
            else navigate(target as any);
        }
        option.afterLink?.(context);
        return true;
    };

    return (
        <div className="detail-json">
            {visibleBlocks.map((block, blockIndex) => (
                <section key={blockIndex} className="detail-json__block" style={getBlockStyle(block)}>
                    <TitleBlock
                        item={block}
                        slot={
                            block.sectionSlot
                                ? slots[block.sectionSlot]?.({
                                      block,
                                      colIndex: -1,
                                      columnCount,
                                      columnLabelWidthMap,
                                      blockLayoutMaps
                                  })
                                : undefined
                        }
                    />
                    {block.list.length ? (
                        <div
                            className="detail-json__grid"
                            style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                                // 旧 Vue 的 gridStyle 只消费全局 gap；block.gap 虽保留在类型中但没有覆盖行为。
                                gap: `${config?.gap?.[0] ?? 12}px ${config?.gap?.[1] ?? 12}px`
                            }}
                        >
                            {block.list.map((item, itemIndex) => {
                                const value = resolveValue(item);
                                // 旧 Vue 对外暴露列表索引，业务 rowSlot 会据此定位原始字段配置。
                                const context = {
                                    item,
                                    value,
                                    colIndex: itemIndex,
                                    columnCount,
                                    columnLabelWidthMap,
                                    blockLayoutMaps
                                };
                                const custom = item.slot ? slots[item.slot]?.(context) : undefined;
                                const layout = blockLayoutMaps[blockIndex]?.[itemIndex];
                                return (
                                    <div
                                        key={`${String(item.key)}-${itemIndex}`}
                                        className={`detail-json__item ${block.labelPlacement === "top" ? "detail-json__item-vertical" : "detail-json__item-horizontal"}${item.class ? ` ${item.class}` : ""}`}
                                        style={{ gridColumn: `span ${Math.min(item.span ?? 1, columnCount)}` }}
                                    >
                                        {item.rowSlot ? (
                                            // 声明 rowSlot 后即由插槽完全接管；缺失或返回空节点时保持空白。
                                            slots[item.rowSlot]?.(context)
                                        ) : (
                                            <>
                                                {item.label ? (
                                                    <div
                                                        className="detail-json__label"
                                                        style={getLabelStyle(block, item, layout?.colIndex ?? 0)}
                                                        onClick={event =>
                                                            (item.onLabelClick || item.onClick)?.(
                                                                eventContext(item, value, event)
                                                            )
                                                        }
                                                    >
                                                        {item.label}
                                                    </div>
                                                ) : null}
                                                <div
                                                    className={`detail-json__value${item.copyable ? " is-copyable" : ""}${item.link ? " is-link" : ""}`}
                                                    onClick={async event => {
                                                        if (await handleCopy(item, value, event)) return;
                                                        if (handleLink(item, value, event)) return;
                                                        if (item.onValueClick || item.onClick)
                                                            (item.onValueClick || item.onClick)?.(
                                                                eventContext(item, value, event)
                                                            );
                                                    }}
                                                >
                                                    {/* 声明 slot 后不使用普通值兜底，保持 Vue 空插槽语义。 */}
                                                    {item.slot ? custom : value}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </section>
            ))}
        </div>
    );
}

function getColumnCount() {
    if (typeof window === "undefined") return 4;
    // 与旧 Vue useBreakpoints 保持一致：xl 4 列、lg 3 列、md/sm 2 列、xs 1 列。
    if (window.innerWidth >= 1920) return 4;
    if (window.innerWidth >= 1200) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
}

function normalizeCopyable(value: DetailJsonColumn["copyable"]): CopyableOption | null {
    if (!value) return null;
    return value === true ? { enabled: true } : { enabled: true, ...value };
}

function normalizeLink(value: DetailJsonColumn["link"]): LinkOption | null {
    if (!value) return null;
    return value === true ? { enabled: true } : { enabled: true, target: "_self", ...value };
}

function getBlockStyle(block: DetailJsonBlock): CSSProperties {
    const style = block.blockStyle;
    if (!style) return {};
    return {
        backgroundColor: style.background ?? "#fff",
        borderRadius: style.borderRadius ?? "8px",
        padding: typeof style.padding === "number" ? `${style.padding}px` : (style.padding ?? "16px"),
        ...style
    };
}
